import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Calculator, DollarSign, Clock, Package, Wrench, Droplet, Droplets, Zap, Flame, CheckCircle2, XCircle } from 'lucide-react';

const GeneralCostPerKgCard = ({
  productConsumption = [],
  receptionTotals = {}, 
  utilityTotals = {},
  utilityTypeTotals = {},
  workedHours = {},
  indicators = null
}) => {
  // Costo por hora desde indicadores parametrizados
  const costoPorHora = Number(indicators?.costoPorHora || 0);

  // Cálculos de costos
  const kilosProcesados = Number(receptionTotals?.kilosProcesados || 0);

  // 1. Costo de productos (detergentes)
  const costoProductos = Array.isArray(productConsumption)
    ? productConsumption.reduce((acc, item) => acc + Number(item?.totalCosto || 0), 0)
    : 0;
  const litrosProductos = Array.isArray(productConsumption)
    ? productConsumption.reduce((acc, item) => acc + Number(item?.totalUsado || 0), 0)
    : 0;
  const costoProductosPorKg = kilosProcesados > 0 ? costoProductos / kilosProcesados : 0;
  const costoDetergenteReal = indicators?.isCostoKilo ? Number(indicators.costoKilo || 0) : costoProductosPorKg;
  // Convertir litros a miligramos (1 L = 1,000 mg)
  const miligramosProductos = litrosProductos * 1000;
  const miligramosPorKg = kilosProcesados > 0 ? miligramosProductos / kilosProcesados : 0;

  // 2. Costo de servicios por tipo (utilities)
  const costoAguaFria = Number(utilityTypeTotals?.agua?.costos || 0);
  const consumoAguaFria = Number(utilityTypeTotals?.agua?.consumoLitros || 0);
  const costoAguaFriaPorKg = kilosProcesados > 0 ? costoAguaFria / kilosProcesados : 0;
  const litrosAguaFriaPorKg = kilosProcesados > 0 ? consumoAguaFria / kilosProcesados : 0;

  const costoAguaCaliente = Number(utilityTypeTotals?.aguaCaliente?.costos || 0);
  const consumoAguaCaliente = Number(utilityTypeTotals?.aguaCaliente?.consumoLitros || 0);
  const costoAguaCalientePorKg = kilosProcesados > 0 ? costoAguaCaliente / kilosProcesados : 0;
  const litrosAguaCalientePorKg = kilosProcesados > 0 ? consumoAguaCaliente / kilosProcesados : 0;

  const costoGas = Number(utilityTypeTotals?.gas?.costos || 0);
  const consumoGas = Number(utilityTypeTotals?.gas?.consumoLitros || 0);
  const costoGasPorKg = kilosProcesados > 0 ? costoGas / kilosProcesados : 0;
  const litrosGasPorKg = kilosProcesados > 0 ? consumoGas / kilosProcesados : 0;

  const costoElectricidad = Number(utilityTypeTotals?.Electricidad?.costos || 0);
  const consumoElectricidad = Number(utilityTypeTotals?.Electricidad?.consumoLitros || 0);
  const costoElectricidadPorKg = kilosProcesados > 0 ? costoElectricidad / kilosProcesados : 0;
  const litrosElectricidadPorKg = kilosProcesados > 0 ? consumoElectricidad / kilosProcesados : 0;

  // Total de servicios
  const costoServiciosTotalPorKg = costoAguaFriaPorKg + costoAguaCalientePorKg + costoGasPorKg + costoElectricidadPorKg;

  // 3. Costo de horas hombre (usando horas autorizadas)
  const horasAutorizadas = Number(workedHours?.horasAutorizadas || 0);
  const costoHorasHombre = horasAutorizadas * costoPorHora;
  const costoHorasHombrePorKg = kilosProcesados > 0 ? costoHorasHombre / kilosProcesados : 0;
  const kgPorHora = horasAutorizadas > 0 ? kilosProcesados / horasAutorizadas : 0;

  // 4. Costo total por kg
  const costoTotalPorKg = costoDetergenteReal + costoServiciosTotalPorKg + costoHorasHombrePorKg;

  const cardBase = "relative overflow-hidden bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-sm hover:shadow-lg rounded-2xl transition-all duration-300";

  return (
    <Card className={cardBase}>
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500" />
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2.5">
          <div className="flex items-center justify-center h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm">
            <Calculator className="h-4.5 w-4.5 text-white" />
          </div>
          Indicadores De Gestión Por Kilogramo de Ropa
        </CardTitle>
      </CardHeader>

      <CardContent>
        {/* Información de contexto */}
        <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200/60 dark:border-purple-700/60">
          <div className="text-sm text-purple-700 dark:text-purple-300">
            Basado en <strong>{kilosProcesados.toLocaleString()} kg</strong> procesados
          </div>
        </div>

        {/* Tabla de indicadores */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900/40 dark:to-purple-800/20">
                <th rowSpan="2" className="text-center p-3 font-semibold text-gray-700 dark:text-gray-300 border-b-2 border-r border-gray-300 dark:border-gray-600">
                  Indicador
                </th>
                <th colSpan="2" className="text-center p-3 font-semibold text-gray-700 dark:text-gray-300 border-b border-r border-gray-300 dark:border-gray-600">
                  Medición Real
                </th>
                <th colSpan="2" className="text-center p-3 font-semibold text-gray-700 dark:text-gray-300 border-b border-r border-gray-300 dark:border-gray-600">
                  Indicador (Meta)
                </th>
                <th colSpan="4" className="text-center p-3 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600">
                  Desviación
                </th>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-gray-300 dark:border-gray-600">
                  Costo (COP/kg)
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-r border-gray-300 dark:border-gray-600">
                  Consumo
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-gray-300 dark:border-gray-600">
                  Costo (COP/kg)
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-r border-gray-300 dark:border-gray-600">
                  Consumo
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-gray-300 dark:border-gray-600">
                  Costo %
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-gray-300 dark:border-gray-600">
                  Costo
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-gray-300 dark:border-gray-600">
                  Consumo %
                </th>
                <th className="text-right p-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-gray-300 dark:border-gray-600">
                  Consumo
                </th>
              </tr>
            </thead>
            <tbody>
              {/* ── Sección: Servicios Públicos ── */}
              <tr>
                <td colSpan="9" className="px-3 pt-4 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-1 rounded-full bg-blue-400 dark:bg-blue-500" />
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-500 dark:text-blue-400">Servicios Públicos</span>
                    <div className="flex-1 h-px bg-blue-100 dark:bg-blue-900/30" />
                  </div>
                </td>
              </tr>

              {/* Agua Fría */}
              <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Droplets className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Agua Fría</span>
                  </div>
                </td>
                <td className="p-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {costoAguaFriaPorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                  {litrosAguaFriaPorKg.toFixed(2)} L/kg
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                  {indicators?.aguaFria?.valorPesos
                    ? indicators.aguaFria.valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                    : 'No configurado'}
                </td>
                <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                  {indicators?.aguaFria?.consumoEsperado
                    ? `${indicators.aguaFria.consumoEsperado.toFixed(2)} L/kg`
                    : '-'}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaFria?.valorPesos ? (
                    <span className={`font-semibold ${costoAguaFriaPorKg <= indicators.aguaFria.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((costoAguaFriaPorKg - indicators.aguaFria.valorPesos) / indicators.aguaFria.valorPesos * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaFria?.valorPesos ? (
                    <span className={`font-semibold ${costoAguaFriaPorKg <= indicators.aguaFria.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(costoAguaFriaPorKg - indicators.aguaFria.valorPesos).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaFria?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosAguaFriaPorKg <= indicators.aguaFria.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((litrosAguaFriaPorKg - indicators.aguaFria.consumoEsperado) / indicators.aguaFria.consumoEsperado * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaFria?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosAguaFriaPorKg <= indicators.aguaFria.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(litrosAguaFriaPorKg - indicators.aguaFria.consumoEsperado).toFixed(2)} L/kg
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>

              {/* Agua Caliente */}
              <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Droplet className="h-4 w-4 text-rose-400 dark:text-rose-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Agua Caliente</span>
                  </div>
                </td>
                <td className="p-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {costoAguaCalientePorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                  {litrosAguaCalientePorKg.toFixed(2)} L/kg
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                  {indicators?.aguaCaliente?.valorPesos
                    ? indicators.aguaCaliente.valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                    : 'No configurado'}
                </td>
                <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                  {indicators?.aguaCaliente?.consumoEsperado
                    ? `${indicators.aguaCaliente.consumoEsperado.toFixed(2)} L/kg`
                    : '-'}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaCaliente?.valorPesos ? (
                    <span className={`font-semibold ${costoAguaCalientePorKg <= indicators.aguaCaliente.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((costoAguaCalientePorKg - indicators.aguaCaliente.valorPesos) / indicators.aguaCaliente.valorPesos * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaCaliente?.valorPesos ? (
                    <span className={`font-semibold ${costoAguaCalientePorKg <= indicators.aguaCaliente.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(costoAguaCalientePorKg - indicators.aguaCaliente.valorPesos).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaCaliente?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosAguaCalientePorKg <= indicators.aguaCaliente.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((litrosAguaCalientePorKg - indicators.aguaCaliente.consumoEsperado) / indicators.aguaCaliente.consumoEsperado * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.aguaCaliente?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosAguaCalientePorKg <= indicators.aguaCaliente.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(litrosAguaCalientePorKg - indicators.aguaCaliente.consumoEsperado).toFixed(2)} L/kg
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>

              {/* Gas */}
              <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-400 dark:text-orange-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Gas</span>
                  </div>
                </td>
                <td className="p-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {costoGasPorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                  {litrosGasPorKg.toFixed(3)} m<sup>3</sup>/kg
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                  {indicators?.gas?.valorPesos
                    ? indicators.gas.valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                    : 'No configurado'}
                </td>
                <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                  {indicators?.gas?.consumoEsperado
                    ? `${indicators.gas.consumoEsperado.toFixed(3)} m3/kg`
                    : '-'}
                </td>
                <td className="p-3 text-right">
                  {indicators?.gas?.valorPesos ? (
                    <span className={`font-semibold ${costoGasPorKg <= indicators.gas.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((costoGasPorKg - indicators.gas.valorPesos) / indicators.gas.valorPesos * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.gas?.valorPesos ? (
                    <span className={`font-semibold ${costoGasPorKg <= indicators.gas.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(costoGasPorKg - indicators.gas.valorPesos).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.gas?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosGasPorKg <= indicators.gas.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((litrosGasPorKg - indicators.gas.consumoEsperado) / indicators.gas.consumoEsperado * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.gas?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosGasPorKg <= indicators.gas.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(litrosGasPorKg - indicators.gas.consumoEsperado).toFixed(3)} m<sup>3</sup>/kg
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>

              {/* Electricidad */}
              <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Electricidad</span>
                  </div>
                </td>
                <td className="p-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {costoElectricidadPorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                  {litrosElectricidadPorKg.toFixed(3)} kWh/kg
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                  {indicators?.electricidad?.valorPesos
                    ? indicators.electricidad.valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                    : 'No configurado'}
                </td>
                <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                  {indicators?.electricidad?.consumoEsperado
                    ? `${indicators.electricidad.consumoEsperado.toFixed(3)} kWh/kg`
                    : '-'}
                </td>
                <td className="p-3 text-right">
                  {indicators?.electricidad?.valorPesos ? (
                    <span className={`font-semibold ${costoElectricidadPorKg <= indicators.electricidad.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((costoElectricidadPorKg - indicators.electricidad.valorPesos) / indicators.electricidad.valorPesos * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.electricidad?.valorPesos ? (
                    <span className={`font-semibold ${costoElectricidadPorKg <= indicators.electricidad.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(costoElectricidadPorKg - indicators.electricidad.valorPesos).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.electricidad?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosElectricidadPorKg <= indicators.electricidad.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((litrosElectricidadPorKg - indicators.electricidad.consumoEsperado) / indicators.electricidad.consumoEsperado * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.electricidad?.consumoEsperado ? (
                    <span className={`font-semibold ${litrosElectricidadPorKg <= indicators.electricidad.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(litrosElectricidadPorKg - indicators.electricidad.consumoEsperado).toFixed(3)} kWh/kg
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>

              {/* Subtotal Servicios */}
              {(() => {
                const subtotalReal = costoAguaFriaPorKg + costoAguaCalientePorKg + costoGasPorKg + costoElectricidadPorKg;
                const subtotalMeta = 
                  (Number(indicators?.aguaFria?.valorPesos) || 0) +
                  (Number(indicators?.aguaCaliente?.valorPesos) || 0) +
                  (Number(indicators?.gas?.valorPesos) || 0) +
                  (Number(indicators?.electricidad?.valorPesos) || 0);
                const hasMeta = subtotalMeta > 0;
                const desvPct = hasMeta ? ((subtotalReal - subtotalMeta) / subtotalMeta * 100) : null;
                const desvAbs = hasMeta ? (subtotalReal - subtotalMeta) : null;
                const isOk = hasMeta ? subtotalReal <= subtotalMeta : null;
                return (
                  <tr className="bg-gray-100 dark:bg-gray-800/60 border-y-2 border-gray-300 dark:border-gray-600 font-semibold">
                    <td className="p-3 text-gray-800 dark:text-gray-100 border-r border-gray-300 dark:border-gray-600">
                      <div className="flex items-center gap-2 pl-2">
                        <DollarSign className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                        <span>Subtotal Servicios</span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-gray-900 dark:text-gray-100">
                      {subtotalReal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </td>
                    <td className="p-3 text-right text-gray-400 dark:text-gray-500 border-r border-gray-300 dark:border-gray-600">
                      -
                    </td>
                    <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                      {hasMeta
                        ? subtotalMeta.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                        : '-'}
                    </td>
                    <td className="p-3 text-right text-gray-400 dark:text-gray-500 border-r border-gray-300 dark:border-gray-600">
                      -
                    </td>
                    <td className="p-3 text-right">
                      {desvPct !== null ? (
                        <span className={`font-semibold ${isOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {desvPct.toFixed(1)}%
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="p-3 text-right">
                      {desvAbs !== null ? (
                        <span className={`font-semibold ${isOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {desvAbs.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="p-3 text-right text-gray-400 dark:text-gray-500">-</td>
                    <td className="p-3 text-right text-gray-400 dark:text-gray-500">-</td>
                  </tr>
                );
              })()}

              {/* ── Sección: Detergentes de Lavado ── */}
              <tr>
                <td colSpan="9" className="px-3 pt-4 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-1 rounded-full bg-amber-400 dark:bg-amber-500" />
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-500 dark:text-amber-400">Detergentes de Lavado</span>
                    <div className="flex-1 h-px bg-amber-100 dark:bg-amber-900/30" />
                  </div>
                </td>
              </tr>

              {/* Detergentes de Lavado (Productos) */}
              <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Detergentes de Lavado</span>
                  </div>
                </td>
                <td className="p-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {costoDetergenteReal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                  {miligramosPorKg.toFixed(2)} ml/kg
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                  {indicators?.detergentes?.valorPesos
                    ? indicators.detergentes.valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                    : 'No configurado'}
                </td>
                <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                  {indicators?.detergentes?.consumoEsperado
                    ? `${indicators.detergentes.consumoEsperado.toFixed(2)} ml/kg`
                    : '-'}
                </td>
                <td className="p-3 text-right">
                  {indicators?.detergentes?.valorPesos ? (
                    <span className={`font-semibold ${costoDetergenteReal <= indicators.detergentes.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((costoDetergenteReal - indicators.detergentes.valorPesos) / indicators.detergentes.valorPesos * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.detergentes?.valorPesos ? (
                    <span className={`font-semibold ${costoDetergenteReal <= indicators.detergentes.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(costoDetergenteReal - indicators.detergentes.valorPesos).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.detergentes?.consumoEsperado ? (
                    <span className={`font-semibold ${miligramosPorKg <= indicators.detergentes.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((miligramosPorKg - indicators.detergentes.consumoEsperado) / indicators.detergentes.consumoEsperado * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.detergentes?.consumoEsperado ? (
                    <span className={`font-semibold ${miligramosPorKg <= indicators.detergentes.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(miligramosPorKg - indicators.detergentes.consumoEsperado).toFixed(2)} ml/kg
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>

              {/* ── Sección: Mano de Obra ── */}
              <tr>
                <td colSpan="9" className="px-3 pt-4 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-1 rounded-full bg-emerald-400 dark:bg-emerald-500" />
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-500 dark:text-emerald-400">Mano de Obra</span>
                    <div className="flex-1 h-px bg-emerald-100 dark:bg-emerald-900/30" />
                  </div>
                </td>
              </tr>

              {/* Mano de Obra */}
              <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Mano de Obra</span>
                  </div>
                </td>
                <td className="p-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {costoHorasHombrePorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300 font-medium border-r border-gray-200 dark:border-gray-700">
                  {kgPorHora.toFixed(2)} kg/hora
                </td>
                <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                  {indicators?.manoDeObra?.valorPesos
                    ? indicators.manoDeObra.valorPesos.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                    : 'No configurado'}
                </td>
                <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                  {indicators?.manoDeObra?.consumoEsperado
                    ? `${indicators.manoDeObra.consumoEsperado.toFixed(2)} kg/hora`
                    : '-'}
                </td>
                <td className="p-3 text-right">
                  {indicators?.manoDeObra?.valorPesos ? (
                    <span className={`font-semibold ${costoHorasHombrePorKg <= indicators.manoDeObra.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((costoHorasHombrePorKg - indicators.manoDeObra.valorPesos) / indicators.manoDeObra.valorPesos * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.manoDeObra?.valorPesos ? (
                    <span className={`font-semibold ${costoHorasHombrePorKg <= indicators.manoDeObra.valorPesos
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(costoHorasHombrePorKg - indicators.manoDeObra.valorPesos).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.manoDeObra?.consumoEsperado ? (
                    <span className={`font-semibold ${kgPorHora >= indicators.manoDeObra.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {((kgPorHora - indicators.manoDeObra.consumoEsperado) / indicators.manoDeObra.consumoEsperado * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {indicators?.manoDeObra?.consumoEsperado ? (
                    <span className={`font-semibold ${kgPorHora >= indicators.manoDeObra.consumoEsperado
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      }`}>
                      {(kgPorHora - indicators.manoDeObra.consumoEsperado).toFixed(2)} kg/hora
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>

              {/* Total */}
              {(() => {
                const totalMetaCosto = 
                  (Number(indicators?.aguaFria?.valorPesos) || 0) +
                  (Number(indicators?.aguaCaliente?.valorPesos) || 0) +
                  (Number(indicators?.gas?.valorPesos) || 0) +
                  (Number(indicators?.electricidad?.valorPesos) || 0) +
                  (Number(indicators?.detergentes?.valorPesos) || 0) +
                  (Number(indicators?.manoDeObra?.valorPesos) || 0);
                
                const hasAnyMeta = totalMetaCosto > 0;
                const desvPct = hasAnyMeta ? ((costoTotalPorKg - totalMetaCosto) / totalMetaCosto * 100) : null;
                const desvAbs = hasAnyMeta ? (costoTotalPorKg - totalMetaCosto) : null;
                const isOk = hasAnyMeta ? costoTotalPorKg <= totalMetaCosto : null;

                return (
                  <tr className="bg-gray-100 dark:bg-gray-800/60 font-bold border-t-2 border-gray-300 dark:border-gray-600">
                    <td className="p-3 text-gray-900 dark:text-gray-100 border-r border-gray-300 dark:border-gray-600">
                      TOTAL
                    </td>
                    <td className="p-3 text-right text-gray-900 dark:text-gray-100">
                      {costoTotalPorKg.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                    </td>
                    <td className="p-3 text-right text-gray-500 dark:text-gray-400 border-r border-gray-300 dark:border-gray-600">
                      -
                    </td>
                    <td className="p-3 text-right text-gray-700 dark:text-gray-300">
                      {hasAnyMeta
                        ? totalMetaCosto.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
                        : '-'}
                    </td>
                    <td className="p-3 text-right text-gray-500 dark:text-gray-400 border-r border-gray-300 dark:border-gray-600">
                      -
                    </td>
                    <td className="p-3 text-right">
                      {desvPct !== null ? (
                        <span className={`font-semibold ${isOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {desvPct.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {desvAbs !== null ? (
                        <span className={`font-semibold ${isOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {desvAbs.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3 text-right text-gray-500 dark:text-gray-400">
                      -
                    </td>
                    <td className="p-3 text-right text-gray-500 dark:text-gray-400">
                      -
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Nota informativa */}
        <div className="mt-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/60">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <strong>Nota:</strong> Los valores en <span className="text-green-600 dark:text-green-400 font-semibold">verde</span> indican ahorro (real ≤ meta),
            mientras que los valores en <span className="text-red-600 dark:text-red-400 font-semibold">rojo</span> indican que se superó el indicador meta.
          </p>
        </div>

        {/* Lista de chequeo de cumplimiento */}
        {indicators && (
          <div className="mt-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/60">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Lista de Cumplimiento</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {[
                { label: 'Agua Fría (Costo)', ok: indicators?.aguaFria?.valorPesos ? costoAguaFriaPorKg <= indicators.aguaFria.valorPesos : null },
                { label: 'Agua Fría (Consumo)', ok: indicators?.aguaFria?.consumoEsperado ? litrosAguaFriaPorKg <= indicators.aguaFria.consumoEsperado : null },
                { label: 'Agua Caliente (Costo)', ok: indicators?.aguaCaliente?.valorPesos ? costoAguaCalientePorKg <= indicators.aguaCaliente.valorPesos : null },
                { label: 'Agua Caliente (Consumo)', ok: indicators?.aguaCaliente?.consumoEsperado ? litrosAguaCalientePorKg <= indicators.aguaCaliente.consumoEsperado : null },
                { label: 'Gas (Costo)', ok: indicators?.gas?.valorPesos ? costoGasPorKg <= indicators.gas.valorPesos : null },
                { label: 'Gas (Consumo)', ok: indicators?.gas?.consumoEsperado ? litrosGasPorKg <= indicators.gas.consumoEsperado : null },
                { label: 'Electricidad (Costo)', ok: indicators?.electricidad?.valorPesos ? costoElectricidadPorKg <= indicators.electricidad.valorPesos : null },
                { label: 'Electricidad (Consumo)', ok: indicators?.electricidad?.consumoEsperado ? litrosElectricidadPorKg <= indicators.electricidad.consumoEsperado : null },
                { label: 'Detergentes (Costo)', ok: indicators?.detergentes?.valorPesos ? costoDetergenteReal <= indicators.detergentes.valorPesos : null },
                { label: 'Detergentes (Consumo)', ok: indicators?.detergentes?.consumoEsperado ? miligramosPorKg <= indicators.detergentes.consumoEsperado : null },
                { label: 'Mano de Obra (Costo)', ok: indicators?.manoDeObra?.valorPesos ? costoHorasHombrePorKg <= indicators.manoDeObra.valorPesos : null },
                { label: 'Mano de Obra (Eficiencia)', ok: indicators?.manoDeObra?.consumoEsperado ? kgPorHora >= indicators.manoDeObra.consumoEsperado : null },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-gray-700/50 transition-colors">
                  {item.ok === null ? (
                    <div className="h-4 w-4 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
                  ) : item.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0" />
                  )}
                  <span className={`text-xs ${item.ok === null ? 'text-gray-400 dark:text-gray-500' : item.ok ? 'text-gray-700 dark:text-gray-300' : 'text-red-600 dark:text-red-400 font-medium'}`}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
            {(() => {
              const items = [
                indicators?.aguaFria?.valorPesos ? costoAguaFriaPorKg <= indicators.aguaFria.valorPesos : null,
                indicators?.aguaFria?.consumoEsperado ? litrosAguaFriaPorKg <= indicators.aguaFria.consumoEsperado : null,
                indicators?.aguaCaliente?.valorPesos ? costoAguaCalientePorKg <= indicators.aguaCaliente.valorPesos : null,
                indicators?.aguaCaliente?.consumoEsperado ? litrosAguaCalientePorKg <= indicators.aguaCaliente.consumoEsperado : null,
                indicators?.gas?.valorPesos ? costoGasPorKg <= indicators.gas.valorPesos : null,
                indicators?.gas?.consumoEsperado ? litrosGasPorKg <= indicators.gas.consumoEsperado : null,
                indicators?.electricidad?.valorPesos ? costoElectricidadPorKg <= indicators.electricidad.valorPesos : null,
                indicators?.electricidad?.consumoEsperado ? litrosElectricidadPorKg <= indicators.electricidad.consumoEsperado : null,
                indicators?.detergentes?.valorPesos ? costoDetergenteReal <= indicators.detergentes.valorPesos : null,
                indicators?.detergentes?.consumoEsperado ? miligramosPorKg <= indicators.detergentes.consumoEsperado : null,
                indicators?.manoDeObra?.valorPesos ? costoHorasHombrePorKg <= indicators.manoDeObra.valorPesos : null,
                indicators?.manoDeObra?.consumoEsperado ? kgPorHora >= indicators.manoDeObra.consumoEsperado : null,
              ].filter(v => v !== null);
              const passed = items.filter(Boolean).length;
              const total = items.length;
              return total > 0 ? (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Cumplimiento: <strong className="text-gray-800 dark:text-gray-200">{passed}/{total}</strong> indicadores dentro de meta
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-24 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${passed === total ? 'bg-green-500' : passed >= total * 0.7 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${(passed / total) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-semibold ${passed === total ? 'text-green-600 dark:text-green-400' : passed >= total * 0.7 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                      {((passed / total) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GeneralCostPerKgCard;
